<div class="baa-view-container">

  @if (loading) {
    <div class="loading-overlay">
      <mat-spinner diameter="60"></mat-spinner>
      <p>Loading BAA...</p>
    </div>
  }

  @if (error) {
    <mat-card class="error-card">
      <mat-card-content>
        <mat-icon color="warn">error</mat-icon>
        <h2>{{ error }}</h2>
        <button mat-raised-button color="primary" (click)="goBack()">
          <mat-icon>arrow_back</mat-icon>
          Return to BAAs
        </button>
      </mat-card-content>
    </mat-card>
  }

  @if (!loading && !error && currentBaa) {

    <mat-sidenav-container class="sidenav-container">

      <!-- Actions Sidenav (Left Side) -->
      <mat-sidenav mode="side" opened position="start" class="actions-sidenav">
        <app-entity-actions-sidenav
          title="BAA Actions"
          [actions]="sidenavActions"
          [tabs]="sidenavTabs"
          [selectedTabIndex]="selectedTabIndex"
          (tabSelected)="selectTab($event)">
        </app-entity-actions-sidenav>
      </mat-sidenav>

      <!-- Main Content -->
      <mat-sidenav-content class="form-content" [class.with-viewer]="showDocumentViewer">

        <!-- Archived Version Banner -->
        @if (currentBaa.activeContract === false) {
          <div class="archived-banner">
            <mat-icon>archive</mat-icon>
            <span>Archived Version — Superseded on {{ currentBaa.updatedAt | date:'MM/dd/yyyy' }}</span>
          </div>
        }

        <!-- Header Actions Bar -->
        <div class="header-bar">
          <button mat-button (click)="goBack()">
            <mat-icon>arrow_back</mat-icon>
            Back to BAAs
          </button>

          <div class="header-actions">
            @if (currentDocument) {
              <button mat-stroked-button (click)="toggleDocumentViewer()">
                <mat-icon>{{ showDocumentViewer ? 'visibility_off' : 'visibility' }}</mat-icon>
                {{ showDocumentViewer ? 'Hide' : 'View' }} BAA
              </button>
              <button mat-stroked-button (click)="downloadDocument()">
                <mat-icon>download</mat-icon>
                Download
              </button>
            }
            <button mat-raised-button color="primary" (click)="editBaa()">
              <mat-icon>edit</mat-icon>
              Edit BAA
            </button>
          </div>
        </div>

        <!-- Static Information Section (Always Visible) -->
        <div class="static-info-section">
          <!-- BAA Name Header -->
          <div class="baa-name-header">
            <h1>Business Associate Agreement</h1>
            <div class="baa-metadata">
              <span>BAA ID: {{ currentBaa.baaId || 'N/A' }}</span>
              @if (currentBaa.baaNum) {
                <span class="separator">"</span>
                <span>BAA Number: {{ currentBaa.baaNum }}</span>
              }
            </div>
            @if (currentDocument) {
              <div class="document-preview">
                <mat-icon color="primary">description</mat-icon>
                <span class="main-document-label">BAA Document</span>
                <span class="file-type-badge">{{ currentDocument.fileType.toUpperCase() }}</span>
                <button mat-icon-button
                        [color]="showDocumentViewer ? 'primary' : ''"
                        (click)="toggleDocumentViewer()"
                        matTooltip="Toggle BAA viewer">
                  <mat-icon>{{ showDocumentViewer ? 'visibility_off' : 'visibility' }}</mat-icon>
                </button>
                <div class="version-info">
                  <span class="version-label">Version</span>
                  @if (!currentBaa.versionList || currentBaa.versionList.length === 0) {
                    <span class="version-value">1 of 1</span>
                  } @else {
                    <span class="version-value">{{ currentBaa.version }} of {{ currentBaa.versionList.length }}</span>
                  }
                </div>
                <button mat-stroked-button
                        (click)="selectTab(4)"
                        class="compact-button">
                  BAA History
                </button>
              </div>
            }
          </div>

          <!-- Essential Information -->
          <div class="info-section">
            <div class="section-header">
              <mat-icon>business</mat-icon>
              <h3>Essential Information</h3>
            </div>
            <div class="info-grid essential-grid">
              <div class="info-field">
                <label>Main Document Name</label>
                <div class="value">{{ currentBaa.mainDocumentName || 'N/A' }}</div>
              </div>

              <div class="info-field">
                <label>Status</label>
                <div class="value">
                  <mat-chip-set>
                    <mat-chip [highlighted]="currentBaa.status === 'Active'">
                      {{ currentBaa.status || 'N/A' }}
                    </mat-chip>
                  </mat-chip-set>
                </div>
              </div>

              <div class="info-field">
                <label>Party</label>
                <div class="value">
                  @if (partyVendorNum) {
                    <a class="entity-link" (click)="openEntityLink('vendor', partyVendorNum)">{{ partyName }}</a>
                  } @else {
                    {{ partyName }}
                  }
                </div>
              </div>

              <div class="info-field">
                <label>Business Associate</label>
                <div class="value">
                  @if (businessAssociateVendorNum) {
                    <a class="entity-link" (click)="openEntityLink('vendor', businessAssociateVendorNum)">{{ businessAssociateName }}</a>
                  } @else {
                    {{ businessAssociateName }}
                  }
                </div>
              </div>

              <div class="info-field">
                <label>Covered Entity</label>
                <div class="value">
                  @if (coveredEntityVendorNum) {
                    <a class="entity-link" (click)="openEntityLink('vendor', coveredEntityVendorNum)">{{ coveredEntityName }}</a>
                  } @else {
                    {{ coveredEntityName }}
                  }
                </div>
              </div>

              <div class="info-field">
                <label>Legal Entities</label>
                <div class="value">{{ legalEntitiesDisplay || 'N/A' }}</div>
              </div>

              <div class="info-field">
                <label>Primary Owner</label>
                <div class="value">{{ primaryOwnerName }}</div>
              </div>

              <div class="info-field">
                <label>BA Coordinator</label>
                <div class="value">
                  @if (baCoordinatorId) {
                    <a class="entity-link" (click)="openEntityLink('person', baCoordinatorId)">{{ baCoordinatorName }}</a>
                  } @else {
                    {{ baCoordinatorName }}
                  }
                </div>
              </div>

              <div class="info-field">
                <label>CE Coordinator</label>
                <div class="value">
                  @if (ceCoordinatorId) {
                    <a class="entity-link" (click)="openEntityLink('person', ceCoordinatorId)">{{ ceCoordinatorName }}</a>
                  } @else {
                    {{ ceCoordinatorName }}
                  }
                </div>
              </div>

              <div class="info-field">
                <label>Types of Service</label>
                <div class="value">{{ (currentBaa.typesOfService && currentBaa.typesOfService.length > 0) ? currentBaa.typesOfService.join(', ') : 'N/A' }}</div>
              </div>

              <div class="info-field">
                <label>Departments</label>
                <div class="value">{{ (currentBaa.departments && currentBaa.departments.length > 0) ? currentBaa.departments.join(', ') : 'N/A' }}</div>
              </div>

              <div class="info-field">
                <label>Sites</label>
                <div class="value">{{ sitesDisplay || 'N/A' }}</div>
              </div>

              <div class="info-field">
                <label>Initial Term</label>
                <div class="value">{{ currentBaa.initialTerm ? currentBaa.initialTerm + ' months' : 'N/A' }}</div>
              </div>

              <div class="info-field">
                <label>Renewal Type</label>
                <div class="value">{{ currentBaa.renewalType || 'N/A' }}</div>
              </div>
            </div>
          </div>

          <!-- Timeline & Terms -->
          <div class="info-section">
            <div class="section-header">
              <mat-icon>schedule</mat-icon>
              <h3>Timeline & Terms</h3>
            </div>
            <div class="info-grid terms-grid">
              <div class="info-field">
                <label>Effective Date</label>
                <div class="value">{{ formatDate(currentBaa.effectiveDate) }}</div>
              </div>

              <div class="info-field">
                <label>Expiration Date</label>
                <div class="value">{{ formatDate(currentBaa.expirationDate) }}</div>
              </div>

              <div class="info-field">
                <label>Execution Date</label>
                <div class="value">{{ formatDate(currentBaa.executionDate) }}</div>
              </div>

              <div class="info-field">
                <label>CE Execution Date</label>
                <div class="value">{{ formatDate(currentBaa.ceExecutionDate) }}</div>
              </div>

              <div class="info-field">
                <label>Last Review Date</label>
                <div class="value">{{ formatDate(currentBaa.lastReviewDate) }}</div>
              </div>

              <div class="info-field">
                <label>Next Review Date</label>
                <div class="value">{{ formatDate(currentBaa.nextReviewDate) }}</div>
              </div>
            </div>
          </div>

          <!-- Signatories & Notifications -->
          <div class="info-section">
            <div class="section-header">
              <mat-icon>person</mat-icon>
              <h3>Signatories & Notifications</h3>
            </div>
            <div class="info-grid">
              <div class="info-field">
                <label>BA Signatory</label>
                <div class="value">
                  @if (baSignatoryId) {
                    <a class="entity-link" (click)="openEntityLink('user', baSignatoryId)">{{ baSignatoryName }}</a>
                  } @else {
                    {{ baSignatoryName || 'N/A' }}
                  }
                </div>
              </div>

              <div class="info-field">
                <label>CE Signatory</label>
                <div class="value">
                  @if (ceSignatoryId) {
                    <a class="entity-link" (click)="openEntityLink('user', ceSignatoryId)">{{ ceSignatoryName }}</a>
                  } @else {
                    {{ ceSignatoryName || 'N/A' }}
                  }
                </div>
              </div>

              <div class="info-field">
                <label>BA Notification</label>
                <div class="value">
                  @if (businessAssociateNotificationId) {
                    <a class="entity-link" (click)="openEntityLink('person', businessAssociateNotificationId)">{{ businessAssociateNotificationName }}</a>
                  } @else {
                    {{ businessAssociateNotificationName || 'N/A' }}
                  }
                </div>
              </div>

              <div class="info-field">
                <label>CE Notification</label>
                <div class="value">
                  @if (coveredEntityNotificationId) {
                    <a class="entity-link" (click)="openEntityLink('person', coveredEntityNotificationId)">{{ coveredEntityNotificationName }}</a>
                  } @else {
                    {{ coveredEntityNotificationName || 'N/A' }}
                  }
                </div>
              </div>

              <div class="info-field">
                <label>Secondary Owner</label>
                <div class="value">{{ secondaryOwnerName || 'N/A' }}</div>
              </div>

              <div class="info-field">
                <label>Tertiary Owner</label>
                <div class="value">{{ tertiaryOwnerName || 'N/A' }}</div>
              </div>
            </div>
          </div>

          <!-- Additional Information -->
          @if (currentBaa.description) {
            <div class="info-section">
              <div class="section-header">
                <mat-icon>notes</mat-icon>
                <h3>Description</h3>
              </div>
              <div class="info-field full-width">
                <div class="value description-text">{{ currentBaa.description }}</div>
              </div>
            </div>
          }

          <!-- Boolean Indicators - Only show if at least one is true -->
          @if (currentBaa.missingSignature) {
            <div class="compliance-indicators">
              <mat-chip-set>
                @if (currentBaa.missingSignature) {
                  <mat-chip highlighted class="compact-chip">
                    Missing Signature
                  </mat-chip>
                }
              </mat-chip-set>
            </div>
          }
        </div>

        <!-- Tabbed Content -->
        <mat-tab-group
          [selectedIndex]="selectedTabIndex"
          (selectedIndexChange)="selectTab($event)"
          class="baa-tabs">

          <!-- Tab 0: Associated Contracts -->
          <mat-tab label="Associated Contracts">
            <div class="tab-content">
              @if (currentBaa.contracts && currentBaa.contracts.length > 0) {
                <app-contract-connections
                  [selectedContractList]="currentBaa.contracts">
                </app-contract-connections>
              } @else {
                <mat-card class="empty-state">
                  <mat-card-content>
                    <mat-icon>link</mat-icon>
                    <p>No contracts associated with this BAA</p>
                  </mat-card-content>
                </mat-card>
              }
            </div>
          </mat-tab>

          <!-- BAAs tab - Hidden, may restore later -->
          <!-- COIs tab - Hidden, may restore later -->

          <!-- Tab 2: Attachments -->
          <mat-tab label="Attachments">
            <div class="tab-content">
              @if (entityAttachments && entityAttachments.length > 0) {
                <app-attachment-connections
                  [attachmentObjectArray]="entityAttachments"
                  (attachmentUpdated)="refreshAttachments()">
                </app-attachment-connections>
              } @else {
                <mat-card class="empty-state">
                  <mat-card-content>
                    <mat-icon>attach_file</mat-icon>
                    <p>No attachments for this BAA</p>
                    <button mat-raised-button color="primary" (click)="addAttachment()">
                      <mat-icon>add</mat-icon>
                      Add Attachment
                    </button>
                  </mat-card-content>
                </mat-card>
              }
            </div>
          </mat-tab>

          <!-- Tab 5: Important Dates -->
          <mat-tab label="Important Dates">
            <div class="tab-content">
              @if (entityDates && entityDates.length > 0) {
                <app-critical-date-connections
                  [dateObjectArray]="entityDates"
                  (criticalDateUpdated)="refreshCriticalDates()">
                </app-critical-date-connections>
              } @else {
                <mat-card class="empty-state">
                  <mat-card-content>
                    <mat-icon>event</mat-icon>
                    <p>No important dates for this BAA</p>
                    <button mat-raised-button color="primary" (click)="addCriticalDate()">
                      <mat-icon>add</mat-icon>
                      Add Important Date
                    </button>
                  </mat-card-content>
                </mat-card>
              }
            </div>
          </mat-tab>

          <!-- Tab 6: People -->
          <mat-tab label="People">
            <div class="tab-content">
              <app-people-table
                [contractIds]="currentBaa.contracts || []">
              </app-people-table>
            </div>
          </mat-tab>

          <!-- Tab 7: BAA History (Versions) -->
          <mat-tab label="BAA History">
            <div class="tab-content">
              <app-versions
                entityType="baa"
                [versionList]="currentBaa.versionList || []"
                [currentEntityNum]="currentBaa.baaNum || ''">
              </app-versions>
            </div>
          </mat-tab>

          <!-- Tab 8: Provisions -->
          <mat-tab label="Provisions">
            <div class="tab-content">
              <mat-card class="empty-state">
                <mat-card-content>
                  <mat-icon>gavel</mat-icon>
                  <p>Provisions content coming soon</p>
                </mat-card-content>
              </mat-card>
            </div>
          </mat-tab>

          <!-- Tab 9: Notes -->
          <mat-tab label="Notes">
            <div class="tab-content">
              <app-notes
                [notes]="currentBaa?.notes || []"
                (notesSaved)="saveNotes($event)">
              </app-notes>
            </div>
          </mat-tab>

          <!-- HCP Benefits tab - Hidden, may restore later -->
          <!-- Data Tables tab - Hidden, may restore later -->

          <!-- Tab 8: Assessments -->
          <mat-tab label="Assessments">
            <div class="tab-content">
              <app-assessment-connections
                [entityId]="currentBaa.baaId || ''"
                entityType="BAA"
                [sourceRecord]="currentBaa.friendlyName || currentBaa.coveredEntity || ''">
              </app-assessment-connections>
            </div>
          </mat-tab>

          <!-- Workflow tab - Hidden, may restore later -->
          <!-- Enable Timesheets tab - Hidden, may restore later -->

        </mat-tab-group>

      </mat-sidenav-content>

      <!-- Document Viewer - Using Reusable Component (Right Side) -->
      <mat-sidenav mode="side"
                  position="end"
                  [opened]="showDocumentViewer"
                  class="baa-viewer">
        <app-document-viewer-sidenav
          [document]="currentDocument"
          [title]="'BAA Viewer'"
          (close)="toggleDocumentViewer()"
          (download)="downloadDocument()"
          (openNewTab)="openDocumentInNewTab()">
        </app-document-viewer-sidenav>
      </mat-sidenav>

    </mat-sidenav-container>

  }

</div>
